import { useState, useEffect, useMemo, useRef } from 'preact/hooks'
import { Upload, Loader2 } from 'lucide-preact'
import { TypeAheadDropdown } from '@/components/common/TypeAheadDropdown'
import type { TypeAheadOption } from '@/components/common/TypeAheadDropdown'
import { landingPagesApi } from '@/api/landingPages'
import type { FontCatalogEntryDto } from '@/types/landingPage'
import { useToast } from '@/hooks/useToast'

interface FontsSectionProps {
    workspaceId: string
    webId: string
    /** Current effective heading font family (per-page override or company brand font). */
    headingFamily?: string
    /** Current effective body font family. */
    bodyFamily?: string
    /** Locked while another user holds the chat turn, or mid-generation. */
    disabled?: boolean
    /**
     * Applies the chosen heading/body families. A null family clears that role back to
     * the system-font fallback. Pre-generation the parent stores it as the override that
     * rides into the Generate request; post-generation it re-bakes the draft instantly.
     */
    onApply: (heading: string | null, body: string | null) => void | Promise<void>
    /** Optional (post-generation): ask Webster to tune type scale/spacing for the current fonts. */
    onRefine?: () => void
}

const MAX_FONT_BYTES = 2 * 1024 * 1024
const UPLOAD_BTN_CLASS =
    'flex items-center justify-center gap-2 w-full px-3 py-2 text-sm border-2 border-dashed rounded-lg transition-colors text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 disabled:opacity-50 disabled:pointer-events-none'

export function FontsSection({ workspaceId, webId, headingFamily, bodyFamily, disabled, onApply, onRefine }: FontsSectionProps) {
    const { showError } = useToast()
    const [catalog, setCatalog] = useState<FontCatalogEntryDto[]>([])
    const [customFamilies, setCustomFamilies] = useState<string[]>([])
    const [headingSearch, setHeadingSearch] = useState('')
    const [bodySearch, setBodySearch] = useState('')
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        let active = true
        landingPagesApi.getFontCatalog().then((res) => {
            if (active && res.data) setCatalog(res.data)
        })
        return () => {
            active = false
        }
    }, [])

    // Uploaded custom fonts appear first, then the curated catalog.
    const allFamilies = useMemo<FontCatalogEntryDto[]>(() => {
        // Uploaded fonts first, then the catalog, de-duped by family (case-insensitive)
        // so the picker never renders duplicate option keys.
        const seen = new Set<string>()
        const result: FontCatalogEntryDto[] = []
        for (const entry of [...customFamilies.map((f) => ({ Family: f, Category: 'Uploaded' })), ...catalog]) {
            const key = entry.Family.trim().toLowerCase()
            if (key && !seen.has(key)) {
                seen.add(key)
                result.push(entry)
            }
        }
        return result
    }, [catalog, customFamilies])

    const toOptions = (search: string): TypeAheadOption<FontCatalogEntryDto>[] => {
        const term = search.trim().toLowerCase()
        return allFamilies
            .filter((f) => !term || f.Family.toLowerCase().includes(term))
            .slice(0, 50)
            .map((f) => ({
                key: f.Family,
                label: f.Family,
                data: f,
                render: () => (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-900 dark:text-white">{f.Family}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{f.Category}</span>
                    </div>
                ),
            }))
    }

    const handleUpload = async (e: Event) => {
        const input = e.target as HTMLInputElement
        const file = input.files?.[0]
        input.value = ''
        if (!file) return

        if (!file.name.toLowerCase().endsWith('.woff2')) {
            showError('Unsupported file', 'Please upload a .woff2 font file.')
            return
        }
        if (file.size > MAX_FONT_BYTES) {
            showError('File too large', 'Font files must be 2 MB or smaller.')
            return
        }

        setUploading(true)
        const family = file.name.replace(/\.woff2$/i, '')
        const res = await landingPagesApi.uploadFont(workspaceId, webId, family, file)
        setUploading(false)

        if (res.data) {
            const uploadedFamily = res.data.Family
            setCustomFamilies((prev) => (prev.includes(uploadedFamily) ? prev : [uploadedFamily, ...prev]))
            // Default a just-uploaded custom font to the body role.
            await onApply(headingFamily ?? null, uploadedFamily)
        } else {
            showError('Upload failed', res.error || 'Could not upload the font.')
        }
    }

    return (
        <div className="space-y-3">
            <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Heading font</label>
                <TypeAheadDropdown<FontCatalogEntryDto>
                    minSearchLength={0}
                    disabled={disabled}
                    placeholder={headingFamily || 'System default'}
                    searchValue={headingSearch}
                    onSearchChange={setHeadingSearch}
                    options={toOptions(headingSearch)}
                    onSelect={(opt) => {
                        setHeadingSearch('')
                        onApply(opt.data.Family, bodyFamily ?? null)
                    }}
                    showClearButton
                    onClear={() => onApply(null, bodyFamily ?? null)}
                    noResultsMessage="No matching fonts"
                />
            </div>

            <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Body font</label>
                <TypeAheadDropdown<FontCatalogEntryDto>
                    minSearchLength={0}
                    disabled={disabled}
                    placeholder={bodyFamily || 'System default'}
                    searchValue={bodySearch}
                    onSearchChange={setBodySearch}
                    options={toOptions(bodySearch)}
                    onSelect={(opt) => {
                        setBodySearch('')
                        onApply(headingFamily ?? null, opt.data.Family)
                    }}
                    showClearButton
                    onClear={() => onApply(headingFamily ?? null, null)}
                    noResultsMessage="No matching fonts"
                />
            </div>

            <div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".woff2,font/woff2"
                    className="hidden"
                    onChange={handleUpload}
                />
                <button
                    type="button"
                    disabled={disabled || uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className={UPLOAD_BTN_CLASS}
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Uploading…' : 'Upload a custom font (.woff2)'}
                </button>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    Google Fonts load automatically; upload a .woff2 for a custom or licensed brand font.
                </p>
            </div>

            {onRefine && (
                <div className="pt-1">
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onRefine}
                        className="w-full text-left text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-50"
                    >
                        Ask Webster to fine-tune the type scale & spacing →
                    </button>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                        Sends a message to Webster chat and switches you there so you can watch it work.
                    </p>
                </div>
            )}
        </div>
    )
}
